import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import api from '@/services/api'

export type TraderConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

export interface SubAccount {
  id: string
  name: string
}

export const useTraderMcpStore = defineStore('traderMcp', () => {
  const status = ref<TraderConnectionStatus>('disconnected')
  const toolCount = ref<number>(0)
  const expiresAt = ref<number | null>(null)
  const error = ref<string | null>(null)
  const showPortfolio = ref(false)
  const portfolioData = ref<any>(null)
  const ordersData = ref<any>(null)
  const portfolioLoading = ref(false)
  
  // Account state
  const accounts = ref<SubAccount[]>([])
  const selectedAccount = ref<string | null>(null)
  const primaryAccount = ref<string | null>(null)
  const accountsLoading = ref(false)

  const isConnected = computed(() => status.value === 'connected')
  const isConnecting = computed(() => status.value === 'connecting')
  
  // Get the active account (selected or primary)
  const activeAccount = computed(() => selectedAccount.value || primaryAccount.value)

  let authPopup: Window | null = null
  let authPollInterval: ReturnType<typeof setInterval> | null = null
  let statusRequestId = 0

  function clearAuthPoll(): void {
    if (authPollInterval !== null) {
      clearInterval(authPollInterval)
      authPollInterval = null
    }
  }

  /**
   * Start the OAuth flow by opening the Trader MCP login in a popup.
   */
  async function startOAuthLogin(): Promise<void> {
    // Invalidate any status request that started before this OAuth attempt.
    statusRequestId += 1
    clearAuthPoll()
    status.value = 'connecting'
    error.value = null

    try {
      const response = await api.get('/api/trader-mcp/auth-url')
      const { authUrl } = response.data

      // Open popup for OAuth
      const width = 500
      const height = 600
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      
      authPopup = window.open(
        authUrl,
        'trader-mcp-auth',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      )

      // Listen for the callback message from the popup
      window.addEventListener('message', handleAuthMessage)

      // Poll for popup close — when it closes, check status from server (with retry for race condition)
      authPollInterval = setInterval(async () => {
        if (!authPopup || authPopup.closed) {
          clearAuthPoll()
          window.removeEventListener('message', handleAuthMessage)
          authPopup = null
          // Popup closed — check if OAuth succeeded by querying server
          // Retry a few times because token exchange may still be in progress
          if (status.value === 'connecting') {
            let connected = false
            for (let attempt = 0; attempt < 5; attempt++) {
              await new Promise(r => setTimeout(r, 800))
              await checkStatus()
              if ((status.value as TraderConnectionStatus) === 'connected') {
                connected = true
                break
              }
            }
            if (!connected && status.value === 'connecting') {
              status.value = 'disconnected'
            }
          }
        }
      }, 500)
    } catch (err: any) {
      clearAuthPoll()
      authPopup = null
      status.value = 'error'
      error.value = err.response?.data?.error || 'OAuth başlatılamadı'
    }
  }

  /**
   * Handle the postMessage from the OAuth popup callback page.
   */
  function handleAuthMessage(event: MessageEvent) {
    const data = event.data
    if (data?.type !== 'trader-mcp-auth') return

    clearAuthPoll()
    window.removeEventListener('message', handleAuthMessage)
    authPopup = null

    if (data.success) {
      // The callback only sends this message after token persistence and MCP
      // initialization. A stale status request must not overwrite this state.
      statusRequestId += 1
      status.value = 'connected'
      error.value = null
      // Fetch actual status to get tool count.
      void checkStatus()
    } else {
      status.value = 'error'
      error.value = data.error || 'Portföy girişi başarısız'
    }
  }

  /**
   * Check current connection status from the server.
   */
  async function checkStatus(): Promise<void> {
    const requestId = ++statusRequestId
    try {
      const response = await api.get('/api/trader-mcp/status')
      if (requestId !== statusRequestId) return

      const data = response.data

      if (data.connected) {
        status.value = 'connected'
        toolCount.value = data.toolCount || 0
        expiresAt.value = data.expiresAt || null
      } else {
        status.value = 'disconnected'
        toolCount.value = 0
        expiresAt.value = null
      }
    } catch {
      // Silently fail on status check, but do not let an older request
      // overwrite a newer OAuth result.
      if (requestId !== statusRequestId) return
      status.value = 'disconnected'
    }
  }

  /**
   * Apply a WebSocket status update without allowing an old "disconnected"
   * handshake result to overwrite a successful OAuth callback.
   */
  function syncWebSocketStatus(connected: boolean, nextToolCount = 0): void {
    if (connected) {
      statusRequestId += 1
      status.value = 'connected'
      toolCount.value = nextToolCount
      return
    }

    if (status.value === 'connected' || status.value === 'connecting') return

    statusRequestId += 1
    status.value = 'disconnected'
    toolCount.value = 0
  }

  /**
   * Disconnect from the Trader MCP.
   */
  async function disconnect(): Promise<void> {
    try {
      await api.post('/api/trader-mcp/disconnect')
    } catch {
      // Best effort
    }
    status.value = 'disconnected'
    toolCount.value = 0
    expiresAt.value = null
    error.value = null
    showPortfolio.value = false
    portfolioData.value = null
    ordersData.value = null
  }

  /**
   * Fetch available accounts from Trader MCP.
   */
  async function fetchAccounts(): Promise<void> {
    accountsLoading.value = true
    try {
      const response = await api.get('/api/trader-mcp/accounts')
      const data = response.data?.data
      if (data) {
        accounts.value = data.subAccounts || []
        primaryAccount.value = data.primaryAccount || null
        // Auto-select first account if none selected
        if (!selectedAccount.value && accounts.value.length > 0) {
          selectedAccount.value = accounts.value[0].id
        } else if (!selectedAccount.value && primaryAccount.value) {
          selectedAccount.value = primaryAccount.value
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch accounts:', err)
    } finally {
      accountsLoading.value = false
    }
  }

  /**
   * Set the selected account.
   */
  function setSelectedAccount(accountId: string): void {
    selectedAccount.value = accountId
    // Refresh portfolio when account changes
    if (showPortfolio.value) {
      fetchPortfolio()
    }
  }

  /**
   * Fetch portfolio data from Trader MCP.
   */
  async function fetchPortfolio(): Promise<void> {
    portfolioLoading.value = true
    try {
      const params: Record<string, string> = {}
      if (activeAccount.value) {
        params.subAccount = activeAccount.value
      }
      
      const [portfolioRes, ordersRes] = await Promise.all([
        api.get('/api/trader-mcp/portfolio', { params }),
        api.get('/api/trader-mcp/orders', { params })
      ])
      portfolioData.value = portfolioRes.data?.data || null
      ordersData.value = ordersRes.data?.data || null
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Veri alınamadı'
    } finally {
      portfolioLoading.value = false
    }
  }

  /**
   * Open portfolio popup and fetch data.
   */
  async function openPortfolio(): Promise<void> {
    showPortfolio.value = true
    // Fetch accounts first, then portfolio
    await fetchAccounts()
    await fetchPortfolio()
  }

  /**
   * Close portfolio popup.
   */
  function closePortfolio(): void {
    showPortfolio.value = false
  }

  /**
   * Reset on user logout.
   */
  function reset() {
    status.value = 'disconnected'
    toolCount.value = 0
    expiresAt.value = null
    error.value = null
    showPortfolio.value = false
    portfolioData.value = null
    ordersData.value = null
    portfolioLoading.value = false
    accounts.value = []
    selectedAccount.value = null
    primaryAccount.value = null
    accountsLoading.value = false
    if (authPopup && !authPopup.closed) {
      authPopup.close()
    }
    authPopup = null
  }

  return {
    status,
    toolCount,
    expiresAt,
    error,
    showPortfolio,
    portfolioData,
    ordersData,
    portfolioLoading,
    accounts,
    selectedAccount,
    primaryAccount,
    activeAccount,
    accountsLoading,
    isConnected,
    isConnecting,
    startOAuthLogin,
    checkStatus,
    syncWebSocketStatus,
    disconnect,
    fetchAccounts,
    setSelectedAccount,
    fetchPortfolio,
    openPortfolio,
    closePortfolio,
    reset
  }
})
